/**
 * Достаёт человекочитаемый текст ошибки из сообщения бэка.
 *
 * Backend Eplex часто заворачивает детальный текст в `data.error.message` вида
 * «Неопознанная ошибка Data: {...,"err":"..."}» (JSON внутри строки, с
 * unicode-escape-последовательностями). Эта функция вынимает вложенный
 * `err`/`error`/`message` и отдаёт готовый к показу текст — `JSON.parse` сам
 * раскодирует `\uXXXX`.
 *
 * Возвращает `null` для пустого входа — вызывающий подставит локализованный
 * fallback (не показываем англоязычное «Unknown error»).
 */
export function extractReadableError(rawMessage?: string | null): string | null {
  if (!rawMessage) return null;
  const jsonMatch = /Data:\s*(\{.*\})/.exec(rawMessage);
  if (!jsonMatch) return rawMessage;

  try {
    const parsed = JSON.parse(jsonMatch[1]) as Record<string, unknown>;
    const errorText = parsed.err ?? parsed.error ?? parsed.message;
    if (typeof errorText === "string") return errorText;
    return rawMessage;
  } catch {
    return rawMessage;
  }
}

/**
 * Достаёт читаемый текст ошибки из axios-подобного объекта ошибки.
 * Заглядывает в `error.response.data.error.message` (обёртка ЛК-прокси),
 * раскодирует вложенный `Data: {...}` через {@link extractReadableError},
 * затем пробует `error.message`. Возвращает `null`, если понятного текста нет.
 */
export function extractReadableErrorFrom(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;
  const axiosLike = error as {
    response?: { data?: { error?: { message?: string }; detail?: string } };
    message?: string;
  };
  // `detail` — формат problem+json самого бэкенда ЛК (Symfony HttpException):
  // там уже лежит человеческий текст, готовый к показу.
  return (
    extractReadableError(axiosLike.response?.data?.error?.message) ??
    extractReadableError(axiosLike.response?.data?.detail) ??
    extractReadableError(axiosLike.message)
  );
}
